import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { TankProfileSettingsComponent } from '@components/tank-profile-settings/tank-profile-settings.component';
import { ProfileSelectorConfig } from '@interfaces/profile';
import { ITankProfile } from '@interfaces/tankProfile';
import { TankProfileService } from '@services/tank-profile.service';
import { selectLastSelectedTankProfile } from '@state/selectors/tank-profile.selectors';
import { selectCsoId, selectUserInformation } from '@state/selectors/user-profile.selectors';
import { TapAppStore } from '@state/tap-state';
import { Subject, BehaviorSubject, takeUntil, tap, map, withLatestFrom } from 'rxjs';
import * as TankProfileActions from '@state/actions/tank-profile.actions';
import { AppDialogService } from '@services/app-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { AuthService } from '@services/auth.service';
import { SecurityPolicies } from '@services/auth-variables';
import { IDirty } from '@interfaces/idirty.interface';

@Component({
  selector: 'app-tank-profile-settings',
  templateUrl: './tank-profiles.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TankProfilesComponent implements OnDestroy, IDirty, OnInit {
  @ViewChild('tankProfileSettings') tankProfileSettings: TankProfileSettingsComponent;

  csoId$ = this.store.select(selectCsoId);
  destroy$: Subject<boolean> = new Subject<boolean>();

  hasTankProfiles = false;
  editingTankProfile = false;
  editingName = false;
  previewingProfile = false;

  tankProfileConfig: ProfileSelectorConfig = {
    profileIdField: 'tankProfileId',
    profileNameField: 'tankProfileName',
    isPersonalField: 'isPersonal',
    isPrivateField: 'isPrivate',
    ownerField: 'owner',
    organizationNameField: 'organizationName',
    translationPrefix: 'TankProfile',
  };

  currentEditingTankProfile: ITankProfile;
  currentEditingTankProfile$ = new BehaviorSubject<ITankProfile>(null);
  currentTankProfileId = 0;
  currentTankProfile$ = 
  this.store.select(selectLastSelectedTankProfile).pipe(
    withLatestFrom(this.auth.passesPolicyOf(SecurityPolicies.RequiresManageTankProfile)),
    tap(([profile, canManage]) => {
      if (profile) {
        this.currentTankProfileId = profile.tankProfileId;
        this.changeDetectorRef.markForCheck();
      }
    }),
    map(([profile, canManage]) => {
      if (profile) {
        //setup if is in readonly state.
        profile = { ...profile, isReadOnly: !profile.isPersonal && !canManage };
      }
      return profile || null;
    }),
    takeUntil(this.destroy$)
  );

  tankProfilesData$ = new BehaviorSubject<ITankProfile[]>([]);
  loading = false;
  tankProfiles$ = this.tankProfilesData$.asObservable();

  get panelState(): 'noTankProfiles' | 'tankProfiles' | 'edit' {
    return !this.hasTankProfiles
      ? 'noTankProfiles'
      : this.editingTankProfile
      ? 'edit'
      : 'tankProfiles';
  }

  /** Preview shows unsaved edits on the grid, which only means something for the applied profile -
   *  for any other one the editor header offers Apply to grid instead. */
  get editProfileIsCurrentProfile(): boolean {
    return this.currentEditingTankProfile?.tankProfileId === this.currentTankProfileId;
  }

  get currentTankProfileFormGroup(): FormGroup | UntypedFormGroup {
    return this.tankProfileSettings?.tankProfileForm || new FormGroup({});
  }

  isDirty(): boolean {
    return !this.previewingProfile && (this.tankProfileSettings?.isDirty || false);
  }

  constructor(
    private readonly appStore: TapAppStore,
    private readonly store: Store<AppState>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly tankProfileService: TankProfileService,
    private readonly appDialogService: AppDialogService,
    private readonly translate: TranslateService,
    private readonly auth: AuthService
  ) {}

  ngOnInit(): void {
    console.log('TankProfilesComponent initialized');
    this.loadTankProfiles();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onEditingProfile(profile: ITankProfile) {
    this.setEdit(profile);
  }

  onCreateNewProfile() {
    this.loading = true;
    this.changeDetectorRef.markForCheck();
    
    this.tankProfileService
      .createTankProfile(this.currentEditingTankProfile)
      .subscribe({
        next: (newProfile) => {
          if (newProfile) {
            this.refreshTankProfiles();
            this.onApplyProfile(newProfile);
          }
        },
        error: (err) => {
          this.loading = false;
          this.changeDetectorRef.markForCheck();
          console.error('Failed to create tank profile', err);
        }
      });
  }

  onApplyProfile(profile: ITankProfile) {
    if (!profile) return;
     this.appStore.store.dispatch(new TankProfileActions.ActiveTankProfileChanged(profile));
  }

  onCopyProfile(profile: ITankProfile) {
    this.loading = true;
    this.changeDetectorRef.markForCheck();
    
    this.tankProfileService.copyTankProfile(profile.tankProfileId, profile.isTVP).subscribe({
      next: (newProfile) => {
        if (newProfile) {
          this.refreshTankProfiles();
          if (!this.isDirty()) {
            this.onApplyProfile(newProfile);
          } else {
            this.appDialogService.showGeneralMessage(
              this.translate.instant('TankProfile.SaveBeforeApplyingCopiedProfile'),
            );
          }
        }
      },
      error: (err) => {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
        console.error('Failed to copy tank profile', err);
      }
    });
  }

  onDeleteProfile(tankProfile: ITankProfile) {
    if (!tankProfile) return;

    this.deleteTankProfile(tankProfile);
  }

  onCancel() {
    // Reset the current editing profile to null
    this.setEdit(null);
  }

  //runs when the rename field is rendered, so it opens focused with the current name selected
  @ViewChild('profileNameInput') set profileNameInput(input: ElementRef<HTMLInputElement>) {
    if (!input) return;
    setTimeout(() => {
      input.nativeElement.focus();
      input.nativeElement.select();
    });
  }

  //what Cancel restores - the name as it was when this edit opened, which may be an earlier confirmed rename
  private nameBeforeEdit: string;
  //the name as last loaded or saved, which is what decides whether a rename counts as a change
  private savedProfileName: string;

  onEditProfileNameClicked() {
    this.nameBeforeEdit = this.displayedProfileName;
    this.editingName = true;
  }

  /** The name the header shows: a confirmed but unsaved rename wins over the stored name. */
  get displayedProfileName(): string {
    return (
      this.tankProfileSettings?.tankProfileForm?.get('tankProfileName')?.value ||
      this.currentEditingTankProfile?.tankProfileName
    );
  }

  get canConfirmProfileName(): boolean {
    const control = this.currentTankProfileFormGroup.get('tankProfileName');
    const value: string = control?.value ?? '';
    if (!control || control.invalid || value.trim().length < 3) return false;
    if (value === this.nameBeforeEdit || value === this.savedProfileName) return true;

    //the duplicate check is debounced, so a result only counts once it is for this exact value
    const settings = this.tankProfileSettings;
    return (
      settings?.lastCheckedName === value &&
      !settings.isAnyValidationChecking &&
      settings.validationState.tankProfileName.isValid
    );
  }

  onConfirmProfileNameClicked(event?: Event) {
    //Enter and Escape belong to the name field here, not to the dialog around it
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canConfirmProfileName) return;
    this.editingName = false;
    this.syncProfileNameDirty();
    this.changeDetectorRef.markForCheck();
  }

  onCancelProfileNameClicked(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.editingName = false;
    this.currentTankProfileFormGroup
      .get('tankProfileName')
      .patchValue(this.nameBeforeEdit ?? this.savedProfileName, {
        emitEvent: true,
      });
    this.syncProfileNameDirty();
    // Reset validation state in child component
    if (this.tankProfileSettings && this.tankProfileSettings.resetValidationState) {
      this.tankProfileSettings.resetValidationState();
    }
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
    }, 100);
  }

  /**
   * Save is enabled by the form's dirty flag, so the name control is dirty exactly when it differs
   * from the saved name. Never disable the control to end an edit: Angular leaves disabled controls
   * out of the form's dirty check, and a rename on its own would then leave Save disabled.
   */
  private syncProfileNameDirty() {
    const control = this.currentTankProfileFormGroup.get('tankProfileName');
    if (control.value === this.savedProfileName) control.markAsPristine();
    else control.markAsDirty();
  }

  onTankProfileSaved(tankProfile: ITankProfile) {
    this.savedProfileName = this.currentTankProfileFormGroup.getRawValue().tankProfileName;
    this.refreshTankProfiles();
  }

  onPreviewProfileClicked() {
    this.previewingProfile = true;
  }

  deleteTankProfile(tankProfile: ITankProfile) {
    this.loading = true;
    this.changeDetectorRef.markForCheck();
    
    this.tankProfileService.deleteTankProfile(tankProfile).subscribe({
      next: (value) => {
        if (!value || value.status !== 0) {
          this.loading = false;
          this.changeDetectorRef.markForCheck();
          return;
        }
        //if the profile deleted was the profile currently being edited, we need to set the editing profile to the user's last selected profile
        if (this.currentTankProfileId === tankProfile.tankProfileId) {
          //apply the next profile in the list or null if no profiles left
          const nextProfile = this.tankProfilesData$.value.find(
            profile => profile.tankProfileId !== tankProfile.tankProfileId
          );
          this.onApplyProfile(nextProfile || null);
        } else if (this.currentEditingTankProfile?.tankProfileId === tankProfile.tankProfileId) {
          //if the user's last selected profile was deleted, we set the editing profile to null
          this.setEdit(null);
        }
        this.refreshTankProfiles();
      },
      error: (err) => {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
        console.error('Failed to delete tank profile', err);
      }
    });
  }

  getTankNameErrorText(): string | null {
    if (
      !this.tankProfileSettings?.validationState?.tankProfileName?.isValid &&
      this.tankProfileSettings?.validationState?.tankProfileName?.errorMessage
    )
      return this.translate.instant(
        this.tankProfileSettings.validationState.tankProfileName.errorMessage
      );

    if (this.currentTankProfileFormGroup.get('tankProfileName')?.errors) {
      if (this.currentTankProfileFormGroup.get('tankProfileName')?.errors?.required) {
        return this.translate.instant('TankProfile.Form.TankProfileNameRequired');
      }
      if (this.currentTankProfileFormGroup.get('tankProfileName')?.errors?.minlength) {
        return this.translate.instant('TankProfile.Form.TankProfileNameMinLength', {
          minLength: 3,
        });
      }
      if (this.currentTankProfileFormGroup.get('tankProfileName')?.errors?.maxlength) {
        return this.translate.instant('TankProfile.Form.TankProfileNameMaxLength', {
          maxLength: 100,
        });
      }
    }
    return null;
  }

  setEdit(tankProfile: ITankProfile) {
    this.editingName = false;
    this.savedProfileName = tankProfile?.tankProfileName;
    this.currentEditingTankProfile = tankProfile;
    this.currentEditingTankProfile$.next(tankProfile);
  }

  showTankProfileHasDependentsError() {
    this.appDialogService.showErrorMessage(
      this.translate.instant('TankProfile.HasDependentsError') ||
        'This tank profile is used by scheduled reports. Please remove the dependency before deleting this tank profile.'
    );
  }

  private refreshTankProfiles() {
    this.loadTankProfiles();
  }

  private loadTankProfiles() {
    this.loading = true;
    this.changeDetectorRef.markForCheck();
    
    this.tankProfileService
      .getTankProfiles()
      .pipe(
        withLatestFrom(
          this.store.select(selectUserInformation),
          this.auth.passesPolicyOf(SecurityPolicies.RequiresManageTankProfile)
        ),
        map(([profiles, userInfo, canManageTankProfile]) => {
          const tankProfiles = profiles.map(profile => ({
            ...profile,
            //a TVP-backed profile has no TankProfile row to update, so it can only be viewed or copied
            isReadOnly:
              profile.isReadOnly ||
              profile.isTVP ||
              (profile.userId !== userInfo.userId && !canManageTankProfile),
          }));
          this.hasTankProfiles = tankProfiles.length > 0;
          return tankProfiles;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: profiles => {
          this.tankProfilesData$.next(profiles);
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: err => {
          console.error('Failed to load tank profiles', err);
          this.loading = false;
          this.changeDetectorRef.markForCheck();
          this.appDialogService.showErrorMessage(
            this.translate.instant('TankProfile.LoadError') ||
              'An error occurred while loading tank profiles.'
          );
        },
      });
  }
}
